import { useMemo } from 'react';
import { registerQuery, useQuery as useQueryDFC } from 'data-fetching-client';
import { getFrameworkDataSchemasClient } from 'framework-data-schema-resolvers';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useSkipCrm from 'framework-listing-lib/internal/hooks/useSkipCrm';
const GET_PROPERTY_GROUPS_FIELD_NAME = 'listingLibAllPropertyGroups';
const GET_PROPERTY_GROUPS = registerQuery({
  args: ['objectTypeId', 'showHighlySensitiveProperties'],
  fieldName: GET_PROPERTY_GROUPS_FIELD_NAME,
  fetcher({
    objectTypeId,
    showHighlySensitiveProperties
  }) {
    return getFrameworkDataSchemasClient().then(client => client.properties.getGroups({
      frameworkTypeIdentifier: objectTypeId,
      query: {
        showHighlySensitiveProperties: Boolean(showHighlySensitiveProperties)
      }
    }));
  }
});
const GET_OBJECT_TYPES_FIELD_NAME = 'listingLibAllObjectTypes';
const GET_OBJECT_TYPES = registerQuery({
  fieldName: GET_OBJECT_TYPES_FIELD_NAME,
  fetcher() {
    return getFrameworkDataSchemasClient().then(client => client.frameworkTypes.getObjects());
  }
});
export default function useFetchListingLibData({
  showHighlySensitiveProperties
}) {
  const objectTypeId = useObjectTypeId();
  const skipCrm = useSkipCrm();
  const {
    data: propertyGroupsData,
    loading: propertyGroupsloading,
    error: propertyGroupsError
  } = useQueryDFC(GET_PROPERTY_GROUPS, {
    variables: {
      objectTypeId,
      showHighlySensitiveProperties
    },
    skip: skipCrm,
    onCompleted: () => {
      Metrics.counter('fetch-properties-success', {
        object_type_id: objectTypeId
      }).increment();
    },
    onError: () => {
      Metrics.counter('fetch-properties-failure', {
        object_type_id: objectTypeId
      }).increment();
    }
  });
  const {
    data: objectTypesData,
    loading: objectTypesloading,
    error: objectTypesError
  } = useQueryDFC(GET_OBJECT_TYPES, {
    skip: skipCrm,
    onCompleted: () => {
      Metrics.counter('fetch-object-types-success', {
        object_type_id: objectTypeId
      }).increment();
    },
    onError: () => {
      Metrics.counter('fetch-object-types-failure', {
        object_type_id: objectTypeId
      }).increment();
    }
  });
  const propertyGroups = useMemo(() => {
    if (!propertyGroupsData || !Array.isArray(propertyGroupsData === null || propertyGroupsData === void 0 ? void 0 : propertyGroupsData.listingLibAllPropertyGroups)) {
      return [];
    }
    return propertyGroupsData.listingLibAllPropertyGroups.map(propertyGroup => Object.assign({}, propertyGroup, {
      label: propertyGroup.displayName,
      properties: Array.isArray(propertyGroup.propertyDefinitions) ? propertyGroup.propertyDefinitions.map(definition => Object.assign({}, definition.property, {
        // mostly overlapping string unions
        fieldType: definition.property.fieldType,
        sensitiveDataCategories: definition.property.sensitiveDataCategories,
        type: definition.property.type
      })) : []
    }));
  }, [propertyGroupsData]);
  return useMemo(() => ({
    error: propertyGroupsError || objectTypesError,
    loading: propertyGroupsloading || objectTypesloading,
    properties: propertyGroups.map(propertyGroup => propertyGroup.properties).flat().filter(property => property !== undefined),
    propertyGroups,
    typeDefinitions: objectTypesData !== null && objectTypesData !== void 0 && objectTypesData.listingLibAllObjectTypes && Array.isArray(objectTypesData.listingLibAllObjectTypes) ? objectTypesData.listingLibAllObjectTypes : []
  }), [propertyGroupsError, objectTypesError, propertyGroupsloading, objectTypesloading, propertyGroups, objectTypesData]);
}